/********************************************************************
 * @author:      Kaven
 * @email:       kaven@wuwenkai.com
 * @website:     http://blog.kaven.xyz
 * @file:        [Kaven-Basic] /src/libs/url/RemoveQueryParametersFromURL.ts
 * @create:      2025-06-28 09:09:19.526
 * @modify:      2025-07-11 21:19:12.795
 * @version:     6.0.0
 * @times:       12
 * @lines:       97
 * @copyright:   Copyright © 2018-2025 Kaven. All Rights Reserved.
 * @description: [description]
 * @license:     
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 * 
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 * 
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 ********************************************************************/

import { ArrayDistinct } from "../array/ArrayDistinct";
import { Strings_Empty } from "../const/Strings";

/**
 * Removes one or more specified query parameters from a given URL.
 *
 * @param url - The URL string from which to remove query parameters.
 * @param names - The names of the query parameters to remove. Accepts one or more parameter names.
 * @returns The URL string with the specified query parameters removed. If no parameter names are provided, returns the original URL.
 *
 * @remarks
 * - The function preserves the URL hash fragment (if present).
 * - Parameter names are automatically encoded using `encodeURIComponent`.
 * - If a parameter appears multiple times, all occurrences are removed.
 * - The function does not mutate the original URL string.
 *
 * @example
 * ```typescript
 * RemoveQueryParametersFromURL('https://example.com/page?foo=1&bar=2#section', 'foo');
 * // Returns: 'https://example.com/page?bar=2#section'
 * ```
 * 
 * @since 4.0.0
 * @version 2021-12-11
 */
export function RemoveQueryParametersFromURL(url: string, ...names: string[]) {
    if (!names || names.length < 1) {
        return url;
    }

    names = ArrayDistinct(names);
    names = names.map(encodeURIComponent);

    let hash = Strings_Empty;

    const index = url.indexOf("#");
    if (index !== -1) {
        hash = url.substring(index);
        url = url.substring(0, index);
    }

    for (const name of names) {
        // prefer to use l.search if you have a location/link object
        const urlParts = url.split("?");
        if (urlParts.length >= 2) {
            const prefix = name + "=";
            const pars = urlParts[1].split(/[&;]/g);

            // reverse iteration as may be destructive
            for (let i = pars.length; i-- > 0;) {
                // idiom for string.startsWith
                if (pars[i].lastIndexOf(prefix, 0) !== -1) {
                    pars.splice(i, 1);
                } else if (pars[i] === name) {
                    pars.splice(i, 1);
                }
            }

            url = urlParts[0] + (pars.length > 0 ? "?" + pars.join("&") : "");
        }
    }

    return url + hash;
}
